export const TYPE_IDS = ["cope", "flex", "chaos", "signal", "liquid", "viral", "burn", "hodl", "rug", "ai", "moon", "stake", "rekt"] as const;
export type TypeId = (typeof TYPE_IDS)[number];

export const TYPE_META: Record<
  TypeId,
  { name: string; ink: readonly [number, number, number, number]; chip: string }
> = {
  cope: { name: "COPE", ink: [196, 88, 104, 255], chip: "COPE" },
  flex: { name: "FLEX", ink: [220, 172, 56, 255], chip: "FLEX" },
  chaos: { name: "CHAOS", ink: [120, 84, 156, 255], chip: "CHAOS" },
  signal: { name: "SIGNAL", ink: [64, 180, 188, 255], chip: "SIG" },
  liquid: { name: "LIQUID", ink: [56, 116, 148, 255], chip: "LIQ" },
  viral: { name: "VIRAL", ink: [80, 168, 88, 255], chip: "VIR" },
  burn: { name: "BURN", ink: [232, 104, 48, 255], chip: "BURN" },
  hodl: { name: "HODL", ink: [132, 200, 232, 255], chip: "HODL" },
  rug: { name: "RUG", ink: [96, 64, 120, 255], chip: "RUG" },
  ai: { name: "AI", ink: [150, 164, 186, 255], chip: "AI" },
  moon: { name: "MOON", ink: [236, 206, 96, 255], chip: "MOON" },
  stake: { name: "STAKE", ink: [112, 196, 96, 255], chip: "STAKE" },
  rekt: { name: "REKT", ink: [132, 120, 170, 255], chip: "REKT" },
};

/**
 * 1.5 super, 0.5 resist. The original six-type cycle plus three market rivalries; the frontier
 * types slot in with two strengths and two or three weaknesses each:
 * BURN (token burns) melts VIRAL growth and HODL ice, drowns in LIQUID, gets outpriced by SIGNAL;
 * HODL (frozen hands) freezes FLEX and LIQUID, cracks under BURN, CHAOS and RUG;
 * RUG (pulled liquidity) cuts SIGNAL and HODL, loses to COPE and VIRAL word of mouth.
 * The Undernet adds two more:
 * AI (autonomous agents) automates SIGNAL and orders CHAOS, but catches VIRAL malware and
 * overheats under BURN; SIGNAL barely scratches it.
 * MOON (lift-off) leaves RUG and COPE behind, but CHAOS volatility and HODL gravity drag it down;
 * FLEX cannot impress it.
 * The Sidechain adds the last two:
 * STAKE (locked-up growth: roots, vines, blossoms) drinks LIQUID, locks RUG out and outlasts REKT,
 * but BURN sets it alight and VIRAL blight eats it; LIQUID and other STAKE barely water it.
 * REKT (wrecked, haunted, gone to zero) crashes MOON and humbles FLEX, but COPE lays it to rest
 * and STAKE outlasts it; a RUG or a CHAOS swing cannot hurt what is already at zero.
 */
const SUPER: ReadonlyArray<readonly [TypeId, TypeId]> = [
  ["cope", "flex"],
  ["flex", "chaos"],
  ["chaos", "signal"],
  ["signal", "liquid"],
  ["liquid", "viral"],
  ["viral", "cope"],
  ["liquid", "cope"],
  ["flex", "viral"],
  ["chaos", "liquid"],
  ["burn", "viral"],
  ["burn", "hodl"],
  ["liquid", "burn"],
  ["signal", "burn"],
  ["hodl", "flex"],
  ["hodl", "liquid"],
  ["chaos", "hodl"],
  ["rug", "signal"],
  ["rug", "hodl"],
  ["cope", "rug"],
  ["viral", "rug"],
  ["ai", "signal"],
  ["ai", "chaos"],
  ["viral", "ai"],
  ["burn", "ai"],
  ["moon", "rug"],
  ["moon", "cope"],
  ["chaos", "moon"],
  ["hodl", "moon"],
  ["stake", "liquid"],
  ["stake", "rug"],
  ["stake", "rekt"],
  ["burn", "stake"],
  ["viral", "stake"],
  ["rekt", "moon"],
  ["rekt", "flex"],
  ["cope", "rekt"],
];

const RESIST: ReadonlyArray<readonly [TypeId, TypeId]> = [
  ["cope", "cope"],
  ["flex", "flex"],
  ["signal", "viral"],
  ["liquid", "flex"],
  ["viral", "chaos"],
  ["chaos", "flex"],
  ["burn", "burn"],
  ["burn", "liquid"],
  ["hodl", "hodl"],
  ["flex", "hodl"],
  ["rug", "rug"],
  ["chaos", "rug"],
  ["ai", "ai"],
  ["signal", "ai"],
  ["moon", "moon"],
  ["flex", "moon"],
  ["stake", "stake"],
  ["liquid", "stake"],
  ["stake", "burn"],
  ["rekt", "rekt"],
  ["rug", "rekt"],
  ["chaos", "rekt"],
  ["rekt", "hodl"],
];

export function typeMod(atk: TypeId, def: TypeId[]): number {
  let m = 1;
  for (const d of def) {
    if (SUPER.some(([a, b]) => a === atk && b === d)) m *= 1.5;
    else if (RESIST.some(([a, b]) => a === atk && b === d)) m *= 0.5;
  }
  return m;
}

/** Net damage hints include both defensive types; no random rolls or battle mutation. */
export function bestTypesAgainst(def: TypeId[]): { types: TypeId[]; multiplier: number } {
  const multiplier = Math.max(...TYPE_IDS.map(type => typeMod(type, def)));
  return { types: TYPE_IDS.filter(type => typeMod(type, def) === multiplier), multiplier };
}

export function typeLabel(mod: number): string {
  if (mod >= 2.25) return "Super effective! It printed.";
  if (mod > 1) return "Super effective.";
  if (mod > 0 && mod < 1) return "Not very effective.";
  if (mod === 0) return "No bite.";
  return "";
}
